import type { Screen, Tab } from '../../mobile/src/types';
import { userProfilePath, type AppNavigationSnapshot, type WebRoute } from '../../mobile/src/navigation/web';

const TAB_PATHS: Record<string, Tab> = {
  rank: 'rank',
  play: 'play',
  profile: 'profile',
  settings: 'settings',
};

const DETAIL_PATHS: Record<string, Screen> = {
  friends: 'friends',
  events: 'events',
  notifications: 'notifications',
  communities: 'communities',
  'find-match': 'findMatch',
  'select-course': 'selectCourse',
  score: 'scoreInput',
  'match-result': 'matchResult',
  'match-history': 'matchHistory',
  'matchplay-setup': 'matchplaySetup',
  'golf-club': 'golfClub',
  academy: 'academy',
  partners: 'partners',
  tournament: 'tournament',
  'user-profile': 'userProfile',
};

const DETAIL_TO_PATH = Object.fromEntries(
  Object.entries(DETAIL_PATHS).map(([path, screen]) => [screen, path]),
) as Partial<Record<Screen, string>>;

const LEGAL_PATHS: Record<string, 'terms' | 'privacy' | 'cookies'> = {
  terms: 'terms',
  privacy: 'privacy',
  cookies: 'cookies',
};

export function parsePath(pathname: string): WebRoute {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path.startsWith('/@') && path.length > 2) {
    return { kind: 'profile', handle: decodeURIComponent(path.slice(2)) };
  }

  if (path === '/sign-in') {
    return { kind: 'auth', screen: 'welcome' };
  }
  if (path === '/register') {
    return { kind: 'auth', screen: 'createAccount' };
  }

  const legalMatch = path.match(/^\/(terms|privacy|cookies)$/);
  if (legalMatch) {
    return { kind: 'legal', doc: LEGAL_PATHS[legalMatch[1]] };
  }

  const tabMatch = path.match(/^\/(rank|play|profile|settings)$/);
  if (tabMatch) {
    return { kind: 'main', tab: TAB_PATHS[tabMatch[1]] };
  }

  const detailMatch = path.match(/^\/([a-z-]+)$/);
  if (detailMatch && DETAIL_PATHS[detailMatch[1]]) {
    return { kind: 'detail', screen: DETAIL_PATHS[detailMatch[1]] };
  }

  return { kind: 'main', tab: 'home' };
}

export function buildPath(snapshot: AppNavigationSnapshot): string {
  if (snapshot.screen === 'landing') {
    return '/';
  }
  if (snapshot.screen === 'welcome') {
    return '/sign-in';
  }
  if (snapshot.screen === 'createAccount') {
    return '/register';
  }
  if (snapshot.screen === 'legalTerms') {
    return '/terms';
  }
  if (snapshot.screen === 'legalPrivacy') {
    return '/privacy';
  }
  if (snapshot.screen === 'legalCookies') {
    return '/cookies';
  }
  if (snapshot.screen === 'userProfile') {
    return userProfilePath(snapshot.profileHandle, window.location.pathname);
  }
  if (snapshot.screen !== 'main') {
    const segment = DETAIL_TO_PATH[snapshot.screen];
    return segment ? `/${segment}` : '/';
  }
  if (snapshot.tab === 'home') {
    return '/';
  }
  return `/${snapshot.tab}`;
}

export type { WebRoute };
